import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const FORMULAS = [
  {
    id: 'benchmark',
    name: 'Benchmark EUR',
    category: 'Mercado',
    equation: 'benchmark_EUR = LME_USD / EURUSD',
    variables: [
      { name: 'LME_USD', desc: 'Precio LME del metal en USD por tonelada' },
      { name: 'EURUSD', desc: 'Tipo de cambio Euro / Dolar estadounidense' },
      { name: 'benchmark_EUR', desc: 'Precio de referencia del metal en EUR/t' },
    ],
    example: { values: 'LME_USD = 9200, EURUSD = 1.08', result: 'benchmark_EUR = 9200 / 1.08 = 8519 EUR/t' },
  },
  {
    id: 'sell_abs',
    name: 'Precio de Venta Esperado (descuento absoluto)',
    category: 'Precios',
    equation: 'sell_price = benchmark_EUR - discount_abs_eur_ton',
    variables: [
      { name: 'benchmark_EUR', desc: 'Precio de referencia en EUR/t' },
      { name: 'discount_abs_eur_ton', desc: 'Descuento fijo en EUR/t negociado con el fundidor' },
      { name: 'sell_price', desc: 'Precio de venta esperado en EUR/t' },
    ],
    example: { values: 'benchmark_EUR = 8519, discount_abs = 250', result: 'sell_price = 8519 - 250 = 8269 EUR/t' },
  },
  {
    id: 'sell_pct',
    name: 'Precio de Venta Esperado (descuento porcentual)',
    category: 'Precios',
    equation: 'sell_price = benchmark_EUR x (1 - discount_pct)',
    variables: [
      { name: 'benchmark_EUR', desc: 'Precio de referencia en EUR/t' },
      { name: 'discount_pct', desc: 'Descuento porcentual (p.ej. 0.03 para 3%)' },
      { name: 'sell_price', desc: 'Precio de venta esperado en EUR/t' },
    ],
    example: { values: 'benchmark_EUR = 8519, discount_pct = 0.03', result: 'sell_price = 8519 x (1 - 0.03) = 8263 EUR/t' },
  },
  {
    id: 'recoverable',
    name: 'Valor Recuperable',
    category: 'Precios',
    equation: 'recoverable_value = yield x sell_price_expected',
    variables: [
      { name: 'yield', desc: 'Rendimiento del lote (fraccion, p.ej. 0.92 para 92%)' },
      { name: 'sell_price_expected', desc: 'Precio de venta esperado en EUR/t' },
      { name: 'recoverable_value', desc: 'Valor recuperable por tonelada bruta en EUR/t' },
    ],
    example: { values: 'yield = 0.92, sell_price = 8269', result: 'recoverable_value = 0.92 x 8269 = 7607 EUR/t' },
  },
  {
    id: 'maxbuy',
    name: 'Precio Maximo de Compra',
    category: 'Precios',
    equation: 'max_buy = recoverable_value - logistics - processing - target_margin - risk_buffer',
    variables: [
      { name: 'recoverable_value', desc: 'Valor recuperable por tonelada bruta en EUR/t' },
      { name: 'logistics', desc: 'Coste de logistica en EUR/t' },
      { name: 'processing', desc: 'Coste de procesamiento en EUR/t' },
      { name: 'target_margin', desc: 'Margen objetivo en EUR/t' },
      { name: 'risk_buffer', desc: 'Buffer de riesgo en EUR/t' },
      { name: 'max_buy', desc: 'Precio maximo de compra recomendado en EUR/t' },
    ],
    example: { values: 'recoverable = 7607, logistics = 80, processing = 120, margin = 200, buffer = 255', result: 'max_buy = 7607 - 80 - 120 - 200 - 255 = 6952 EUR/t' },
  },
  {
    id: 'riskbuffer',
    name: 'Buffer de Riesgo (Dinámico — VaR 95%)',
    category: 'Riesgo',
    equation: 'risk_buffer = σ_combined × √days × 1.65 × benchmark_EUR_today × yield',
    variables: [
      { name: 'eurPrice_i', desc: 'Precio del metal en EUR en el día i: LME_USD_i / EURUSD_i — usando el tipo de cambio histórico de ese mismo día, no el actual' },
      { name: 'daily_return_i', desc: '(eurPrice_i − eurPrice_{i−1}) / eurPrice_{i−1} — captura tanto la volatilidad del precio LME en USD como los movimientos del EUR/USD y su correlación' },
      { name: 'σ_combined', desc: 'stdDev(daily_returns) — sigma diaria combinada que incorpora vol LME USD + vol EUR/USD + correlación entre ambas fuentes de riesgo' },
      { name: '√days', desc: 'Raíz cuadrada de los días estimados hasta la venta (ajuste temporal de la volatilidad, regla de la raíz del tiempo)' },
      { name: '1.65', desc: 'Factor estadístico para nivel de confianza del 95% (z-score del percentil 95 de la distribución normal)' },
      { name: 'benchmark_EUR_today', desc: 'Precio actual del metal en EUR/t (LME_USD_hoy / EURUSD_hoy) — solo para escalar el buffer al precio vigente' },
      { name: 'yield', desc: 'Rendimiento del lote — fracción de metal recuperable del scrap tras procesamiento' },
      { name: 'risk_buffer', desc: 'Pérdida potencial máxima en EUR/t al 95% de confianza durante el período de inventario, capturando ambas fuentes de riesgo' },
    ],
    example: { values: 'σ_combined = 0.014 (1.4%), days = 30, benchmark_EUR = 8519, yield = 0.92', result: 'risk_buffer = 0.014 × √30 × 1.65 × 8519 × 0.92 = 0.014 × 5.477 × 1.65 × 7838 = 994 EUR/t' },
    note: 'La σ_combined se calcula sobre retornos diarios de eurPrice_i = LME_USD_i / EURUSD_i (últimos 90 días). Esto captura automáticamente la volatilidad del precio LME en USD, la volatilidad del tipo de cambio EUR/USD y la correlación entre ambas — sin necesidad de modelarlas por separado.',
  },
  {
    id: 'riskbuffer_manual',
    name: 'Buffer de Riesgo (Manual)',
    category: 'Riesgo',
    equation: 'risk_buffer = (manual_pct / 100) × benchmark_EUR × yield',
    variables: [
      { name: 'manual_pct', desc: 'Porcentaje fijo introducido manualmente por el usuario (p.ej. 3%)' },
      { name: 'benchmark_EUR', desc: 'Precio de referencia actual en EUR/t' },
      { name: 'yield', desc: 'Rendimiento del lote' },
      { name: 'risk_buffer', desc: 'Reserva monetaria fija para cubrir caída de precio en EUR/t' },
    ],
    example: { values: 'manual_pct = 3, benchmark_EUR = 8519, yield = 0.92', result: 'risk_buffer = (3/100) × 8519 × 0.92 = 235 EUR/t' },
  },
  {
    id: 'exposure',
    name: 'Exposicion Total',
    category: 'Riesgo',
    equation: 'exposure_EUR = SUM(qty_t x yield x benchmark_EUR)',
    variables: [
      { name: 'qty_t', desc: 'Cantidad en toneladas de cada lote en inventario' },
      { name: 'yield', desc: 'Rendimiento del lote' },
      { name: 'benchmark_EUR', desc: 'Precio de referencia actual en EUR/t' },
      { name: 'exposure_EUR', desc: 'Exposicion total al mercado en euros' },
    ],
    example: { values: '2 lotes: (10t x 0.92 x 8519) + (5t x 0.88 x 7200)', result: 'exposure = 78375 + 31680 = 110055 EUR' },
  },
  {
    id: 'potloss',
    name: 'Perdida Potencial',
    category: 'Riesgo',
    equation: 'potential_loss = exposure_EUR x shock_pct',
    variables: [
      { name: 'exposure_EUR', desc: 'Exposicion total al mercado en euros' },
      { name: 'shock_pct', desc: 'Porcentaje de caida del escenario de shock' },
      { name: 'potential_loss', desc: 'Perdida maxima estimada en euros' },
    ],
    example: { values: 'exposure = 110055 EUR, shock_pct = 0.05', result: 'potential_loss = 110055 x 0.05 = 5503 EUR' },
  },
];

const CATEGORY_COLORS = {
  Mercado: { bg: 'color-mix(in srgb, #3b82f6 12%, transparent)', text: '#3b82f6' },
  Precios: { bg: 'color-mix(in srgb, var(--color-accent) 12%, transparent)', text: 'var(--color-accent)' },
  Riesgo: { bg: 'color-mix(in srgb, var(--color-warning) 12%, transparent)', text: 'var(--color-warning)' },
};

function FormulaCard({ formula }) {
  const [expanded, setExpanded] = useState(false);
  const catStyle = CATEGORY_COLORS?.[formula?.category] || CATEGORY_COLORS?.Precios;

  return (
    <div className="rounded-lg border overflow-hidden" style={{ borderColor: 'var(--color-border)', background: 'var(--color-card)' }}>
      <div className="px-5 pt-4 pb-3">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <span
              className="text-xs font-semibold px-2 py-0.5 rounded-full"
              style={{ background: catStyle?.bg, color: catStyle?.text, fontFamily: '"IBM Plex Sans", sans-serif' }}
            >
              {formula?.category}
            </span>
            <h4 className="font-semibold text-foreground mt-1.5" style={{ fontSize: '0.9375rem', fontFamily: 'var(--font-heading)' }}>
              {formula?.name}
            </h4>
          </div>
        </div>
        <div
          className="rounded-md px-4 py-3"
          style={{ background: 'var(--color-muted)', fontFamily: '"IBM Plex Mono", monospace', fontSize: '0.875rem', color: 'var(--color-foreground)', letterSpacing: '0.01em', overflowX: 'auto', whiteSpace: 'nowrap' }}
        >
          {formula?.equation}
        </div>
      </div>
      <button
        className="w-full flex items-center gap-2 px-5 py-2.5 text-sm transition-standard hover:bg-muted border-t"
        style={{ borderColor: 'var(--color-border)', color: 'var(--color-muted-foreground)', fontFamily: '"IBM Plex Sans", sans-serif' }}
        onClick={() => setExpanded((v) => !v)}
      >
        <Icon name={expanded ? 'ChevronUp' : 'ChevronDown'} size={14} color="currentColor" strokeWidth={2} />
        {expanded ? 'Ocultar detalles' : 'Ver variables y ejemplo'}
      </button>
      {expanded && (
        <div className="px-5 pb-5 space-y-4" style={{ borderTop: '1px solid var(--color-border)' }}>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider mb-2 mt-3" style={{ color: 'var(--color-muted-foreground)', fontFamily: '"IBM Plex Sans", sans-serif' }}>
              Variables
            </p>
            <div className="rounded-md overflow-hidden border" style={{ borderColor: 'var(--color-border)' }}>
              <table className="w-full text-sm">
                <tbody>
                  {formula?.variables?.map((v, i) => (
                    <tr
                      key={i}
                      style={{
                        borderBottom: i < formula?.variables?.length - 1 ? '1px solid var(--color-border)' : 'none',
                        background: i % 2 === 0 ? 'var(--color-muted)' : 'transparent',
                      }}
                    >
                      <td className="px-3 py-2 font-medium align-top" style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '0.8125rem', color: catStyle?.text, whiteSpace: 'nowrap' }}>
                        {v?.name}
                      </td>
                      <td className="px-3 py-2" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.8125rem', color: 'var(--color-muted-foreground)' }}>
                        {v?.desc}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--color-muted-foreground)', fontFamily: '"IBM Plex Sans", sans-serif' }}>
              Ejemplo numerico
            </p>
            <div
              className="rounded-md px-4 py-3 space-y-1"
              style={{ background: 'color-mix(in srgb, var(--color-accent) 6%, var(--color-muted))', border: '1px solid color-mix(in srgb, var(--color-accent) 20%, transparent)' }}
            >
              <p style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '0.8125rem', color: 'var(--color-muted-foreground)' }}>
                {formula?.example?.values}
              </p>
              <p style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: '0.8125rem', color: 'var(--color-foreground)', fontWeight: 600 }}>
                {'->'} {formula?.example?.result}
              </p>
            </div>
          </div>
          {formula?.note && (
            <div className="rounded-md px-4 py-3" style={{ background: 'color-mix(in srgb, var(--color-warning) 8%, var(--color-muted))', border: '1px solid color-mix(in srgb, var(--color-warning) 20%, transparent)' }}>
              <p style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.8125rem', color: 'var(--color-muted-foreground)', lineHeight: 1.5 }}>
                💡 {formula?.note}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function FormulaReferenceTab() {
  return (
    <div className="space-y-4">
      <p className="text-muted-foreground" style={{ fontSize: '0.9rem', fontFamily: '"IBM Plex Sans", sans-serif', lineHeight: 1.6 }}>
        Referencia completa de formulas del motor de precios. Expande cada tarjeta para ver las variables y un ejemplo numerico.
      </p>
      {FORMULAS?.map((f) => (
        <FormulaCard key={f?.id} formula={f} />
      ))}
    </div>
  );
}
